'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { ProductLogoMediumEquipment } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { MemberEquipment, PortalAssetStatus } from '@/mocks/db/types';
import { MY_MANAGED_PLACE_IDS, MY_PORTAL_PERSON_ID } from '../-shared/-api';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { type LedgerFilterTab, LedgerFilterTabs } from '../-shared/-components/LedgerFilterTabs';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { AssetStatusBadge, PersonCell, PlaceCell } from '../-shared/-components/ResourceCells';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useMemberEquipments, usePortalPersons, usePortalPlaces } from '../-shared/-query';
import { formatDate, toMapById } from '../-shared/-utils';
const EQUIPMENTS_PATH = '/individual/portal/lookup/equipments';
const LOOKUP_PATH = '/individual/portal/lookup';
const equipmentsSearchSchema = z.object({
  scope: z.enum(['self', 'managed', 'all']).catch('self'),
  id: z.string().optional()
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/equipments/')({
  validateSearch: equipmentsSearchSchema,
  component: EquipmentsLookupPage
});
function EquipmentsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: equipments,
    isPending
  } = useMemberEquipments();
  const {
    data: persons
  } = usePortalPersons();
  const {
    data: places
  } = usePortalPlaces();
  const personsById = useMemo(() => toMapById(persons), [persons]);
  const placesById = useMemo(() => toMapById(places), [places]);
  const selected = id != null ? equipments?.find(e => e.id === id) ?? null : null;
  const rows = useMemo(() => {
    const all = equipments ?? [];
    if (scope === 'self') {
      return all.filter(e => e.holderPersonId === MY_PORTAL_PERSON_ID);
    }
    if (scope === 'managed') {
      return all.filter(e => e.placeId != null && MY_MANAGED_PLACE_IDS.includes(e.placeId));
    }
    return all;
  }, [equipments, scope]);
  const statusLabel = (status: PortalAssetStatus) => status === 'assigned' ? t('equipments.status.assigned') : t('equipments.status.placed');
  const placeName = (placeId: string | null) => placeId != null ? placesById.get(placeId)?.name ?? null : null;
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('equipments.title'),
        link: {
          to: EQUIPMENTS_PATH,
          search: {
            scope
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumEquipment} title={selected.name} subtitle={selected.assetTag} />
        <LedgerContent>
          <ResourceDetailCard title={t('equipments.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('equipments.detail.category')} value={selected.category} />
              <Field label={t('equipments.detail.status')} value={<AssetStatusBadge status={selected.status} label={statusLabel(selected.status)} />} />
              <Field label={t('equipments.detail.holder')} value={<PersonCell person={selected.holderPersonId != null ? personsById.get(selected.holderPersonId) : null} />} />
              <Field label={t('equipments.detail.assetTag')} value={selected.assetTag} />
              <Field label={t('equipments.detail.place')} value={<PlaceCell name={placeName(selected.placeId)} />} />
              <Field label={t('equipments.detail.purchasedAt')} value={formatDate(selected.purchasedAt)} />
              <Field label={t('equipments.detail.note')} value={selected.note === '' ? '—' : selected.note} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<MemberEquipment>({
    id: 'name',
    header: t('equipments.headers.name'),
    cell: e => <Text variant="label">{e.name}</Text>
  }), ledgerColumn<MemberEquipment>({
    id: 'category',
    header: t('equipments.headers.category'),
    cell: e => e.category
  }), ledgerColumn<MemberEquipment>({
    id: 'assetTag',
    header: t('equipments.headers.assetTag'),
    cell: e => e.assetTag
  }), ledgerColumn<MemberEquipment>({
    id: 'status',
    header: t('equipments.headers.status'),
    cell: e => <AssetStatusBadge status={e.status} label={statusLabel(e.status)} />
  }), ledgerColumn<MemberEquipment>({
    id: 'holder',
    header: t('equipments.headers.holder'),
    cell: e => <PersonCell person={e.holderPersonId != null ? personsById.get(e.holderPersonId) : null} />
  }), ledgerColumn<MemberEquipment>({
    id: 'place',
    header: t('equipments.headers.place'),
    cell: e => <PlaceCell name={placeName(e.placeId)} />
  })];
  const scopeTabs: LedgerFilterTab<'self' | 'managed' | 'all'>[] = [{
    value: 'self',
    label: t('equipments.tabs.self')
  }, {
    value: 'managed',
    label: t('equipments.tabs.managed')
  }, {
    value: 'all',
    label: t('equipments.tabs.all')
  }];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('equipments.title')
    }]} Logo={ProductLogoMediumEquipment} title={t('equipments.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<MemberEquipment> fillHeight columns={columns} data={rows} isPending={isPending} filterTabs={<LedgerFilterTabs tabs={scopeTabs} value={scope} onSelect={s => void navigate({
        search: prev => ({
          ...prev,
          scope: s
        })
      })} ariaLabel={t('common.filterTabsLabel')} />} getRowId={e => e.id} getSearchText={e => `${e.name} ${e.category} ${e.assetTag}`} searchPlaceholder={t('common.search')} onRowSelect={e => void navigate({
        search: prev => ({
          ...prev,
          id: e.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}